/**
 * `<StatusPill>` — the rounded-full status/role badge used across the
 * dashboard (agent status, contact status, run status, KB source/job state,
 * member role, channel kind, …).
 *
 * The experiment design hand-rolls these as `color-mix`-tinted spans rather
 * than the shadcn `Badge`, so this is the single source for that treatment.
 * Pick a semantic `tone`; colored tones derive their fill/border from the
 * dark-mode-aware `--color-*` tokens.
 */

import { cn } from "@cometchat-ai/ui-dashboard";
import type { CSSProperties, ReactNode } from "react";

export type PillTone = "success" | "danger" | "warning" | "info" | "neutral" | "solid";

const TONE_TOKEN: Record<"success" | "danger" | "warning" | "info", string> = {
  success: "var(--color-success)",
  danger: "var(--color-danger)",
  warning: "var(--color-warning)",
  info: "var(--color-info)",
};

const BASE = "inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium";

export function StatusPill({
  tone = "neutral",
  className,
  children,
  ...rest
}: {
  tone?: PillTone;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
  "aria-label"?: string;
}) {
  // Neutral + solid are pure-class; colored tones tint via color-mix so they
  // read correctly against any surface and in dark mode.
  let toneClass = "";
  let style: CSSProperties | undefined;
  if (tone === "neutral") {
    toneClass = "bg-muted text-muted-foreground";
  } else if (tone === "solid") {
    toneClass = "bg-foreground text-background";
  } else {
    const token = TONE_TOKEN[tone];
    style = {
      background: `color-mix(in srgb, ${token} 12%, transparent)`,
      color: token,
    };
  }
  return (
    <span
      className={cn(BASE, toneClass, className)}
      style={style}
      data-testid={rest["data-testid"]}
      aria-label={rest["aria-label"]}
    >
      {children}
    </span>
  );
}
